import type { Profile } from './models';

type PlanSubject = Pick<Profile, 'role' | 'subscriptionTier'>;

/** Skill Providers have their own plan ladder (pro / elite / agency). Everyone else uses the founder ladder. */
export function isProvider(p: PlanSubject) {
  return p.role === 'Skill Provider';
}

/**
 * The single source of truth for what a subscription unlocks. Every gate in the UI and the
 * backend should read from here so a feature can never be visible on one screen and locked on another.
 */
export function planFeatures(p: PlanSubject) {
  const tier = p.subscriptionTier ?? 'free';
  const provider = isProvider(p);
  return {
    provider,
    founder: !provider,
    // Skill Provider plans
    analyticsBasic: provider && ['pro', 'elite', 'agency'].includes(tier),
    analyticsAdvanced: provider && ['elite', 'agency'].includes(tier),
    manageTeam: provider && tier === 'agency',
    verifiedBadge: provider && tier === 'agency',
    // Business Owner plans
    websiteStats: !provider && ['pro', 'enterprise'].includes(tier),
    teamDigest: !provider && ['pro', 'enterprise'].includes(tier),
    boardroom: !provider && tier === 'enterprise',
  };
}

/** True when the account has at least one tool that lives on the Plan Tools page. */
export function hasPlanTools(p: PlanSubject) {
  const f = planFeatures(p);
  return f.analyticsBasic || f.manageTeam || f.websiteStats || f.teamDigest || f.boardroom;
}

/** How many active (not closed) ventures a Skill Provider may be part of at once. */
export function activeProjectCap(p: PlanSubject) {
  if (!isProvider(p)) return Infinity;
  const tier = p.subscriptionTier ?? 'free';
  if (tier === 'elite' || tier === 'agency') return Infinity;
  return tier === 'pro' ? 2 : 1;
}
